/**
 * Catálogo de gerencias donde opera Tecpetrol / FAC y se despliega el PMIS.
 *
 * Decisión de modelado (multi-gerencia F1):
 *   - `GerenciaCodigo` = ISO-3166-1 alpha-2 (CO, AR, PE, MX, EC, BO).
 *   - El atributo `gerenciaId` vive SOLO en `Budget`. Todo lo demás (AFE, PR, PO, SO,
 *     RD, Acta, Contrato, Riesgo, Stakeholder) hereda gerencia vía `Budget.gerenciaId`
 *     a través de la cadena de FKs.
 *   - Cada gerencia define su moneda local (codigo ISO 4217). USD es siempre
 *     una moneda válida en todo gerencia (moneda puente del bloque oil & gas).
 *   - El usuario tiene una lista `gerenciasAccesibles: GerenciaCodigo[]` con las gerencias a
 *     los que tiene acceso. El switcher del Header solo muestra esos.
 *
 * Para añadir una gerencia nuevo, agregar entrada al array `GERENCIAS_DEFAULT` con
 * su `codigo`, `nombre`, `moneda` local y `flag` (emoji). El selector del
 * Header lo recoge automáticamente.
 */

/** Códigos de gerencia soportados por el PMIS. */
export type GerenciaCodigo = "CO" | "AR" | "PE" | "MX" | "EC" | "BO";

/** Información estática de una gerencia. */
export interface GerenciaInfo {
  codigo: GerenciaCodigo;
  /** Nombre visible en el switcher. */
  nombre: string;
  /** Emoji de bandera (Unicode). */
  flag: string;
  /** Moneda local — ISO 4217. */
  monedaLocal: "COP" | "ARS" | "PEN" | "MXN" | "USD" | "BOB";
  /**
   * Símbolo corto de la moneda local para display rápido en headers/badges.
   * No reemplaza el formatter `fmtMoney` que sigue siendo la fuente única
   * de verdad para montos formateados.
   */
  simboloMoneda: string;
  /** ¿Acepta USD como moneda contractual válida adicional a la local? */
  aceptaUsd: boolean;
}

export const GERENCIAS_DEFAULT: GerenciaInfo[] = [
  {
    codigo: "CO",
    nombre: "Colombia",
    flag: "🇨🇴",
    monedaLocal: "COP",
    simboloMoneda: "$",
    aceptaUsd: true,
  },
  {
    codigo: "AR",
    nombre: "Argentina",
    flag: "🇦🇷",
    monedaLocal: "ARS",
    simboloMoneda: "$",
    aceptaUsd: true,
  },
  {
    codigo: "PE",
    nombre: "Perú",
    flag: "🇵🇪",
    monedaLocal: "PEN",
    simboloMoneda: "S/",
    aceptaUsd: true,
  },
  {
    codigo: "MX",
    nombre: "México",
    flag: "🇲🇽",
    monedaLocal: "MXN",
    simboloMoneda: "$",
    aceptaUsd: true,
  },
  {
    codigo: "EC",
    nombre: "Ecuador",
    flag: "🇪🇨",
    // Ecuador es dolarizado oficialmente desde 2000 — USD es la moneda local.
    monedaLocal: "USD",
    simboloMoneda: "$",
    aceptaUsd: true,
  },
  {
    codigo: "BO",
    nombre: "Bolivia",
    flag: "🇧🇴",
    monedaLocal: "BOB",
    simboloMoneda: "Bs",
    aceptaUsd: true,
  },
];

/** Acceso rápido por código (sin recorrer el array). */
export const GERENCIAS_POR_CODIGO: Record<GerenciaCodigo, GerenciaInfo> =
  GERENCIAS_DEFAULT.reduce(
    (acc, p) => {
      acc[p.codigo] = p;
      return acc;
    },
    {} as Record<GerenciaCodigo, GerenciaInfo>,
  );

/**
 * Gerencia por defecto cuando datos legacy no traen el atributo `gerenciaId`.
 * Históricamente el PMIS arrancó en Colombia (Bloque CPO-13), así que ese
 * es el fallback natural para backfills.
 */
export const GERENCIA_DEFAULT: GerenciaCodigo = "CO";

/** Helper: obtiene info de la gerencia o cae al default si el código no existe. */
export function infoGerencia(codigo: GerenciaCodigo | string | undefined): GerenciaInfo {
  if (codigo && codigo in GERENCIAS_POR_CODIGO) {
    return GERENCIAS_POR_CODIGO[codigo as GerenciaCodigo];
  }
  return GERENCIAS_POR_CODIGO[GERENCIA_DEFAULT];
}

// ════════════════════════════════════════════════════════════════════
// Catálogo editable (F4) — persistido en localStorage
// ════════════════════════════════════════════════════════════════════
//
// El array `GERENCIAS_DEFAULT` arriba es el catálogo "de fábrica" (6 gerencias).
// El ADMIN_PMIS puede agregar / editar / desactivar gerencias desde
// `/configuracion/gerencias` — el catálogo activo se persiste en localStorage.
//
// La fusión `GERENCIAS_DEFAULT` + overrides funciona así:
//   - Gerencias nuevos (códigos no presentes en defaults) se agregan al final.
//   - Gerencias editados sobrescriben name/flag/moneda/símbolo/aceptaUsd.
//   - Gerencias "desactivados" tienen `activo: false` → el switcher los oculta.
//   - Reset: borrar la entrada del localStorage vuelve al catálogo de fábrica.

const STORAGE_KEY_GERENCIAS = "pmis.gerenciasCatalogo";

/**
 * GerenciaInfo editable por el ADMIN_PMIS. Acepta cualquier código (no solo los
 * 6 del union literal `GerenciaCodigo`) y agrega flag de activo para el switcher.
 *
 * Usa `Omit<GerenciaInfo, "codigo" | "monedaLocal">` para no restringir códigos
 * ni moneda a los valores hardcoded — el admin puede agregar gerencias y monedas
 * nuevas al catálogo desde /configuracion/gerencias.
 */
export interface GerenciaInfoEditable extends Omit<GerenciaInfo, "codigo" | "monedaLocal"> {
  codigo: string;
  monedaLocal: string;
  activo: boolean;
}

/** Lee el catálogo editado desde localStorage, fusionado con los defaults. */
export function leerGerenciasCatalogo(): GerenciaInfoEditable[] {
  try {
    if (typeof window === "undefined") {
      return GERENCIAS_DEFAULT.map((p) => ({ ...p, activo: true }));
    }
    const raw = localStorage.getItem(STORAGE_KEY_GERENCIAS);
    if (!raw) {
      return GERENCIAS_DEFAULT.map((p) => ({ ...p, activo: true }));
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return GERENCIAS_DEFAULT.map((p) => ({ ...p, activo: true }));
    }
    // Validación defensiva: cada item debe tener al menos `codigo`.
    const items = parsed.filter(
      (x): x is GerenciaInfoEditable =>
        typeof x === "object" &&
        x !== null &&
        typeof (x as { codigo?: unknown }).codigo === "string",
    );
    if (items.length === 0) {
      return GERENCIAS_DEFAULT.map((p) => ({ ...p, activo: true }));
    }
    return items;
  } catch {
    return GERENCIAS_DEFAULT.map((p) => ({ ...p, activo: true }));
  }
}

/** Guarda el catálogo completo y notifica suscriptores. */
export function guardarGerenciasCatalogo(gerenciasAccesibles: GerenciaInfoEditable[]): void {
  try {
    if (typeof window !== "undefined") {
      localStorage.setItem(STORAGE_KEY_GERENCIAS, JSON.stringify(gerenciasAccesibles));
      window.dispatchEvent(new CustomEvent("pmis:gerenciasCatalogo:changed"));
    }
  } catch {
    // localStorage puede fallar (modo privado, quota); aceptamos el caso.
  }
}

/** Restablece el catálogo a los defaults de fábrica. */
export function restablecerGerenciasCatalogo(): void {
  try {
    if (typeof window !== "undefined") {
      localStorage.removeItem(STORAGE_KEY_GERENCIAS);
      window.dispatchEvent(new CustomEvent("pmis:gerenciasCatalogo:changed"));
    }
  } catch {
    // ignore
  }
}
